// src/features/staff/pages/ProfilePage/components/ChangePasswordDialog.jsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Box,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';

export default function ChangePasswordDialog({ 
  open, 
  onClose, 
  onPasswordChange,
  loading = false 
}) {
  const { t } = useTranslation(['staff', 'common']);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  const handleClose = () => {
    if (!loading) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setError('');
      onClose();
    }
  };

  const handleSubmit = async () => {
    // Validate passwords
    if (!currentPassword) {
      setError(t('common:messages.password_required'));
      return;
    }
    
    if (!newPassword) {
      setError(t('staff:profile.new_password_required'));
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError(t('staff:profile.passwords_dont_match'));
      return;
    }
    
    if (newPassword.length < 8) {
      setError(t('staff:profile.password_too_short'));
      return;
    }

    setError('');
    const result = await onPasswordChange(currentPassword, newPassword);
    
    if (result.success) {
      handleClose();
    } else {
      // Handle specific error codes
      if (result.error?.error_code === 'PASSWORD__INVALID') {
        setError(t('staff:profile.current_password_invalid'));
      } else if (result.error?.error_code === 'PASSWORD__MISSING') {
        setError(t('staff:profile.password_missing'));
      } else {
        setError(result.error?.message || t('staff:profile.password_change_failed'));
      }
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          {t('staff:profile.change_password')}
          <IconButton onClick={handleClose} disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            fullWidth
            type="password"
            label={t('staff:profile.current_password')}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            disabled={loading}
            error={!!error && error.includes('current')}
          />
          <TextField
            fullWidth
            type="password"
            label={t('staff:profile.new_password')}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={loading}
            helperText={t('staff:profile.password_requirements')}
          />
          <TextField
            fullWidth
            type="password"
            label={t('staff:profile.confirm_password')}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading}
          />
          {error && (
            <Alert severity="error">
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t('common:buttons.cancel')}
        </Button>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : t('staff:profile.change_password')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}